import type { AgentSuggestion } from '@travel-planner/shared/schemas';
import type { LucideIcon } from 'lucide-react';
import { Check, Image, Star } from 'lucide-react';
import { useState } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import { Badge, Text } from '@/shared/ui';
import css from './category-card.module.scss';

const styles = pickCss(css, css.root);
const checkboxStyles = pickCss(css, css.checkbox);
const iconFrameStyles = pickCss(css, css.iconFrame);
const fitBadgeStyles = pickCss(css, css.fitBadge);
const provenanceTagStyles = pickCss(css, css.provenanceTag);
const matchedChipStyles = pickCss(css, css.matchedChip);

const TOP_CHIP_COUNT = 3;
const STAR_COUNT = 5;

// Match score at or above this threshold renders green; below renders amber.
const FIT_STRONG_THRESHOLD = 70;

export type CategoryCardWarning = 'over-budget' | 'mixed-currency';

export type CategoryCardIconOverlay = {
  Icon: LucideIcon;
  tone?: 'primary' | 'muted';
};

export type CategoryCardProvenance = 'ai-picked' | 'from-catalog';

export type CategoryCardProps = {
  product: AgentSuggestion;
  selected: boolean;
  multiSelect: boolean;
  onToggle: (id: string) => void;
  warning?: CategoryCardWarning | null;
  // Top relevance reason surfaced under the chip row (e.g. "client named
  // Sublime Comporta in the email"). Optional: not every preselect has one.
  whyMatched?: string;
  // Stay cards in the design carry a star rating (1-5) — the rest don't.
  // Renders only when supplied AND priceCents is non-null per the issue.
  starRating?: number | null;
  // Stay-tab preselected card surfaces an inline "Best match" pill next
  // to the title (design `ss-c1-bm`).
  isBestMatch?: boolean;
  // Replaces the 64×64 image thumb with a 40×40 colored icon-frame.
  // Used by the transport tab where every card is icon-only (design
  // `tr-c1-icf` / `tr-c2-icf` / `tr-c3-icf`).
  iconOverlay?: CategoryCardIconOverlay;
  // Provenance of this suggestion (pen `gJJV0` AI-picked vs `Z7zVMd`
  // from-catalog). Controls purple accent rail and provenance tag.
  provenance?: CategoryCardProvenance;
};

const formatPrice = (
  priceCents: number | null,
  currency: string | null
): string | null => {
  if (priceCents == null || currency == null) return null;
  const amount = (priceCents / 100).toLocaleString('en-US', {
    maximumFractionDigits: 0,
  });
  return `${amount} ${currency}`;
};

const formatDuration = (durationMinutes: number | null): string | null => {
  if (durationMinutes == null) return null;
  if (durationMinutes >= 60 && durationMinutes % 60 === 0) {
    const hours = durationMinutes / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  return `${durationMinutes} min`;
};

const buildMeta = (product: AgentSuggestion): string => {
  const parts: string[] = [];
  if (product.city) parts.push(product.city);
  const price = formatPrice(product.priceCents, product.currency);
  if (price) parts.push(price);
  const duration = formatDuration(product.durationMinutes);
  if (duration) parts.push(duration);
  return parts.join(' · ');
};

export const CategoryCard = ({
  product,
  selected,
  multiSelect,
  onToggle,
  warning = null,
  whyMatched,
  starRating = null,
  isBestMatch = false,
  iconOverlay,
  provenance,
}: CategoryCardProps) => {
  const meta = buildMeta(product);
  const chips = product.matchedHashtags.slice(0, TOP_CHIP_COUNT);
  const showStars =
    starRating != null && product.priceCents != null && starRating > 0;
  const showMatchedChips = chips.length > 0;
  const showWarningChip = warning != null;
  const OverlayIcon = iconOverlay?.Icon;
  const isAiPicked = provenance === 'ai-picked';
  const isStrong = product.matchScore >= FIT_STRONG_THRESHOLD;
  // Pen `ss-c1-bm` shows the "Best match" pill alone — no Fit % beside it
  // (the accommodation matcher doesn't return a numeric score).
  const showFitBadge = !isBestMatch;
  // Many catalog rows have a `thumbnail` field that points at an
  // UploadThing key whose file 404s on the public CDN. When that happens
  // the browser renders its broken-image glyph; flip to our placeholder
  // span instead so the row looks clean.
  const [imageBroken, setImageBroken] = useState(false);
  const showImage = Boolean(product.imageUrl) && !imageBroken;

  return (
    <button
      aria-label={multiSelect ? `Select ${product.title}` : undefined}
      aria-pressed={selected}
      className={styles({
        isSelected: selected,
        isAiPicked,
      })}
      onClick={() => onToggle(product.id)}
      type="button"
    >
      {OverlayIcon ? (
        <span
          aria-hidden="true"
          className={iconFrameStyles({
            toneMuted: iconOverlay?.tone === 'muted',
          })}
        >
          <OverlayIcon size={18} />
        </span>
      ) : showImage ? (
        // Empty alt: card title + city already labels the row; the image
        // is decorative for a consultant scanning by name. onError flips
        // to the placeholder if the UTFS CDN returns 4xx.
        <img
          alt=""
          className={css.thumb}
          onError={() => setImageBroken(true)}
          src={product.imageUrl ?? undefined}
        />
      ) : (
        <span aria-hidden="true" className={css.thumb}>
          <Image size={22} />
        </span>
      )}
      <span className={css.body}>
        <span className={css.titleRow}>
          <Text size="sm" weight="semibold">
            {product.title}
          </Text>
          {isBestMatch && <Badge variant="active">Best match</Badge>}
          {showFitBadge && (
            <span
              aria-label={`Fit ${Math.round(product.matchScore)}%`}
              className={fitBadgeStyles({ isStrong, isWeak: !isStrong })}
              role="img"
            >
              Fit {Math.round(product.matchScore)}%
            </span>
          )}
          {provenance === 'ai-picked' && (
            <span className={provenanceTagStyles({ isAi: true })}>
              AI-picked
            </span>
          )}
          {provenance === 'from-catalog' && (
            <span className={provenanceTagStyles({ isCatalog: true })}>
              From catalog
            </span>
          )}
        </span>
        {showStars && (
          <output aria-label={`${starRating} stars`} className={css.stars}>
            {Array.from({ length: STAR_COUNT }, (_, index) => (
              <Star
                aria-hidden="true"
                className={joinCss(
                  css.star,
                  index < starRating ? css.starFilled : undefined
                )}
                key={`star-${product.id}-${index}`}
                size={12}
              />
            ))}
          </output>
        )}
        {meta && (
          <Text size="xs" variant="muted">
            {meta}
          </Text>
        )}
        {showMatchedChips && (
          <span className={css.chips}>
            {chips.map((chip) => (
              <span className={matchedChipStyles()} key={chip}>
                {chip.startsWith('#') ? chip : `#${chip}`}
              </span>
            ))}
          </span>
        )}
        {showWarningChip && (
          <span className={css.chips}>
            {warning === 'over-budget' && (
              <Badge variant="error">Over budget</Badge>
            )}
            {warning === 'mixed-currency' && (
              <Badge variant="error">Mixed currency</Badge>
            )}
          </span>
        )}
        {whyMatched && <span className={css.reason}>{whyMatched}</span>}
      </span>
      {/*
       * Pen `B3oRd` renders a check square on every card variant — filled
       * violet `Asjsd` when picked, hollow `oj1Qr` with #CBD5E1 stroke
       * otherwise. The `multiSelect` flag still drives interaction (single
       * vs multi pick); the indicator itself is always visible so the
       * picker affordance reads the same across Stay / Activities / Food /
       * Transport tabs.
       */}
      <span
        aria-hidden="true"
        className={checkboxStyles({ isChecked: selected })}
      >
        {selected && <Check size={14} />}
      </span>
    </button>
  );
};
